import React from 'react'
import styled from 'styled-components'
import Plaatjie from '@ubo/plaatjie'
import { Helmet } from 'react-helmet-async'
import { IGatsbyImageData } from 'gatsby-plugin-image'

// Components
import ParseContent from 'components/shared/ParseContent'

// Interface
import { BannerProps } from 'components/flex/Banner/Shell'

const LANDING_IMAGE_SIZES = '(max-width: 767px) 100vw, 1000px'

const getLandingImageData = (
  image: BannerProps['fields']['image']
): IGatsbyImageData | undefined =>
  image?.localFile?.childImageSharp?.gatsbyImageData as
    | IGatsbyImageData
    | undefined

const BannerWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  max-width: 1600px;
  @media (min-width: 992px) {
    height: 675px;
  }
`

const Image = styled(Plaatjie)`
  height: 100%;
  top: 0;
  right: 0;
  position: absolute !important;

  @media (min-width: 1850px) {
    max-width: 1000px;
  }
  @media (max-width: 1849px) {
    max-width: 1000px;
  }
  @media (max-width: 991px) {
    & img {
      max-width: 850px;
    }
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 0px;
    bottom: 0px;
    width: 2000px;
    background-color: ${({ theme }) => theme.color.light};
  }

  &:after {
    content: '';
    position: absolute;
    right: -220px;
    top: 0px;
    bottom: 0px;
    width: 220px;
    background: rgb(255, 255, 255);
    background: linear-gradient(
      270deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5648634453781513) 29%,
      rgba(255, 255, 255, 1) 90%,
      rgba(255, 255, 255, 1) 100%
    );
  }

  @media (min-width: 768px) {
    background-color: ${({ theme }) => theme.color.light};
  }
  @media (max-width: 767px) {
    background: rgb(255, 255, 255, 0.85);
  }
  @media (max-width: 991px) {
    padding-top: 2rem;
  }
`

const Content = styled(ParseContent)`
  color: ${({ theme }) => theme.color.dark};

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    font-family: ${({ theme }) => theme.font.family.primary};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    @media (min-width: 992px) {
      font-size: 60px;
      line-height: 60px;
      margin-bottom: 2.5rem;
    }
    @media (max-width: 991px) {
      font-size: 40px;
      line-height: 40px;
      margin-bottom: 1.5rem;
    }
  }

  & p {
    @media (min-width: 992px) {
      font-size: 26px;
      line-height: 30px;
    }
    @media (max-width: 991px) {
      font-size: 20px;
      line-height: 25px;
    }
  }
`

const BannerLanding: React.FC<BannerProps> = ({ fields }) => {
  const imageData = getLandingImageData(fields?.image)
  const webpSource = imageData?.images?.sources?.find(
    (source) => source.type === 'image/webp'
  )

  return (
    <section>
      {webpSource?.srcSet && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            type="image/webp"
            href={imageData?.images?.fallback?.src}
            {...{
              imagesrcset: webpSource.srcSet,
              imagesizes: LANDING_IMAGE_SIZES,
            }}
          />
        </Helmet>
      )}
      <BannerWrapper>
        <div className="container h-100">
          <div className="row h-100">
            <div className="col-md-7 order-md-2">
              <Image
                alt="banner"
                image={fields?.image}
                loading="eager"
                sizes={LANDING_IMAGE_SIZES}
              />
            </div>
            <ContentWrapper className="col-md-5 position-relative h-100 d-flex align-items-center">
              <Content content={fields.description || ''} />
            </ContentWrapper>
          </div>
        </div>
      </BannerWrapper>
    </section>
  )
}

export default BannerLanding
